import { getNavigationPath, NavigationEntityType } from '@AppModels/hooks/useAINavigationResolver';
import { useQueryClient } from '@tanstack/react-query';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import React, { createContext, ReactNode, useContext, useEffect, useId, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useHistory } from 'react-router';
import { toast } from 'sonner';
import { useStreamingResponseMutation } from '../../../../hooks/useStreamingResponseMutation';
import { useErrorMessages, useFullscreenModal, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import { searchRequestToQueryStringParams } from '../../../../models/UiUtils';
import { useAdminAccountQuery } from '../../../../queries';
import { useAIAssistantCreateConversationMutation } from '../../../../queries/AI/useAIAssistantCreateConversationMutation';
import {
	getConversationChatMessageQueryKey,
	getLastAIConversationChatMessage,
	invalidateAIConversationChatMessage,
	useAIConversationChatMessageQuery,
} from '../../../../queries/AIConversation/useAIConversationChatMessageQuery';
import {
	invalidateAllAIConversationFilterQueries,
	useAIConversationFilterQuery,
} from '../../../../queries/AIConversation/useAIConversationFilterQuery';
import { FullScreenModalViewModel } from '../../../../viewmodels/AppViewModels';
import WorkingLionImageUrl from '../../../assets/aiAssistantThinking.png';
import { AIConversationMessage } from '../../../components-v2/ai/AIConversationMessage';
import {
	ChatConversation,
	ChatConversationContent,
	ChatConversationScrollButton,
} from '../../../components-v2/ai/ChatConversation';
import {
	PromptInput,
	PromptInputBody,
	PromptInputFooter,
	PromptInputMessage,
	PromptInputSpeechButton,
	PromptInputSubmit,
	PromptInputTextarea,
	PromptInputTools,
} from '../../../components-v2/ai/PromptInput';
import { ThinkingIndicator } from '../../../components-v2/ai/ThinkingIndicator';
import { Button } from '../../../components-v2/Button';
import { Avatar2 } from '../../../components/Avatar2';
import { CloseButton } from '../../../components/CloseButton';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { ChevronRight } from '../../../components/svgs/icons/ChevronRight';
import { PlusIcon } from '../../../components/svgs/icons/PlusIcon';
import { SparkleIcon } from '../../../components/svgs/icons/SparkleIcon';
import { SparklesIcon } from '../../../components/svgs/icons/SparklesIcon';
import { WarningIcon } from '../../../components/svgs/icons/WarningIcon';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/Tooltip';
import { ROUTES } from '../../../constants/routes';
import { reactQueryClient } from '../../../ReactQueryProvider';
import { brandPrimary } from '../../../styles/colors';
import { bs } from '../../../styles/styles';
import { s } from './styles';
import { syncAIStreamToRQCache } from './utils';

const GOOGLE_BUSINESS_REVIEW_REPLY_ASSISTANT = `GoogleBusinessReviewReplyAssistant`;
const UNIVERSAL_ASSISTANT_NAME = `SiteWideAgent`;
const ADMIN_AGENT_NAME = `AdminAgent`;
const SUCCESS_MEETING_PREP_AGENT_NAME = `SuccessMeetingPrepAgent`;

const CeteraAIDisclaimerDescription = () => (
	<div>
		This content was generated using artificial intelligence and is being provided for illustrative purposes only.
		Content should be reviewed for potential inaccuracies and edited accordingly.
		<br />
		<br />
		Advertising must be submitted to Advertising Review in Pinpoint Global (PPG) for approval prior to posting.
		Financial professionals are responsible for reviewing the AI-generated content carefully, adding required
		disclosures, and ensuring compliance before submission.
		<br />
		<br />
		<strong>Risks:</strong> While Levitate continually measures and optimizes the accuracy of our artificial
		intelligence outputs, it is important to note that AI is considered experimental. The information provided by
		Levitate&apos;s Ask Lev in this email should be used for informational purposes only and should not be solely relied
		upon for critical decision-making.
	</div>
);

/**
 * Displays a compliance disclaimer for Cetera broker dealer accounts when AI-generated
 * email content is used. Required by Cetera for AI Assistant approval.
 * See: https://github.com/Real-Magic/Levitate/issues/16820
 */
const showCeteraAIDisclaimer = () => {
	toast.warning(<strong>AI Content Disclaimer</strong>, {
		description: <CeteraAIDisclaimerDescription />,
		duration: 60000,
		closeButton: true,
		style: {
			minWidth: '700px', // Width chosen to accommodate disclaimer text without excessive wrapping
			left: '50%',
			transform: 'translateX(-50%)',
		},
	});
};

// FE only type for handling error messages in chat
export interface AIChatMessageError extends Api.AIChatMessage {
	error: string;
	chatRequest: Api.AIChatRequest;
}

function CampaignComposerAssistantLayout({
	onClose,
	onNewChat,
	children,
}: {
	onClose?: () => void;
	onNewChat?: () => void;
	children: ReactNode;
}) {
	return (
		<div className={css(s.chatWindow)}>
			{onClose ? <CloseButton styles={[s.closeBtn]} onClick={onClose} /> : null}
			<div className={css(s.chatWindowHeader)}>
				<div className={css(s.chatWindowHeaderStart)}>
					<Avatar2 imgSrc={WorkingLionImageUrl} fallbackText='Leo Avatar' styleDeclaration={s.leoAvatar} />
					<SparklesIcon />
					<span className={css(s.chatWindowHeaderTitle)}>Ask Lev</span>
				</div>
				{onNewChat ? (
					<Tooltip>
						<TooltipTrigger asChild>
							<button
								className={css(s.plusIconButton)}
								style={onClose ? { marginRight: '24px' } : undefined}
								onClick={onNewChat}
							>
								<PlusIcon fillColor={brandPrimary} />
							</button>
						</TooltipTrigger>
						<TooltipContent>New Chat</TooltipContent>
					</Tooltip>
				) : null}
			</div>
			<div className={css(s.chatContent)}>{children}</div>
		</div>
	);
}

const useAssistantConversation = (
	assistantName: string,
	isOpen: boolean,
	impersonationContext?: Api.IImpersonationContext
) => {
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('useAssistantConversation');
	const aiAssistantCreateConversationMutation = useAIAssistantCreateConversationMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`useAssistantConversation-${assistantName}`, error);
		},
	});
	const aiConversationChatMessageQuery = useAIConversationChatMessageQuery({
		enabled: Boolean(aiAssistantCreateConversationMutation.data),
		id: aiAssistantCreateConversationMutation.data?.id,
		impersonationContext,
	});
	useEffect(() => {
		if (isOpen && aiAssistantCreateConversationMutation.isIdle && !aiAssistantCreateConversationMutation.data) {
			aiAssistantCreateConversationMutation.mutate({
				assistantName,
				impersonationContext,
			});
		}
	}, [aiAssistantCreateConversationMutation, isOpen, assistantName, impersonationContext]);
	return {
		conversationId: aiAssistantCreateConversationMutation.data?.id,
		aiConversationChatMessageQuery,
	};
};

export function AIChatAssistantMessage({ children }: { children: ReactNode }) {
	return (
		<div className={css(s.assitantMessage)}>
			<SparkleIcon styles={[s.assistantMessageSparkle]} />
			<div className={css(s.assistantMessageContent)}>{children}</div>
		</div>
	);
}

export function AIChatUserMessage({ children }: { children: ReactNode }) {
	return <div className={css(s.userMessage)}>{children}</div>;
}

interface Params {
	conversationId: string;
	chatRequest: Api.AIChatRequest;
	impersonationContext?: Api.IImpersonationContext;
}

export const useAIAssistantStreamingMutation = ({
	assistantName,
	onSuccess,
}: {
	assistantName: string;
	onError?: (error: Error) => void;
	onSuccess?: (params: Params) => void;
}) => {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const queryClient = useQueryClient();
	const logger = useEventLogging('useAIAssistantStreamingMutation');
	const assistantStreamingMutation = useStreamingResponseMutation({
		mutationFn: (params: Params) =>
			new Promise<Response>((resolve, reject) =>
				userSession.webServiceHelper.callWebServiceRawResponse(
					Api.ImpersonationBroker.composeApiUrl({
						urlPath: `ai/assistant/${assistantName}/conversation/${params.conversationId}/stream`,
						impersonationContext: params.impersonationContext,
					}),
					'POST',
					params.chatRequest,
					resolve,
					reject
				)
			),
		onEvent: (event, params) => {
			if (event.event === 'Error') {
				logger.logEvent('AssistantChatStreamEvent-Error', {
					message: event.data,
				});
			}
			syncAIStreamToRQCache({
				event,
				queryClient,
				queryKey: getConversationChatMessageQueryKey({
					id: params.conversationId,
					impersonationContext: params.impersonationContext,
				}),
				chatRequest: params.chatRequest,
			});
		},
		onError: error => {
			console.error(error);
			logger.logApiError('AssistantChatStreamRequest-Error', error as Api.IOperationResultNoValue);
			errorMessages.pushApiError(error, 'Error while streaming AI assistant response');
		},
		onSuccess: params => {
			const lastMessage = getLastAIConversationChatMessage(params.conversationId, params.impersonationContext);
			if (!(lastMessage as AIChatMessageError)?.error) {
				invalidateAIConversationChatMessage(params.conversationId);
			}
			onSuccess?.(params);
		},
	});
	return assistantStreamingMutation;
};

export function AssistantChatInput({
	placeholder = 'Ask me anything...',
	onSubmit,
	streamingInProgress = false,
}: {
	placeholder?: string;
	onSubmit: (prompt: string) => void;
	streamingInProgress: boolean;
}) {
	const textAreaRef = React.useRef<HTMLTextAreaElement>(null);
	const handlePromptKeyPress = (ev: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (ev.key === 'Enter' && !ev.shiftKey && !ev.nativeEvent.isComposing) {
			ev.preventDefault();
			const prompt = textAreaRef.current.value.trim();
			if (prompt === '') {
				return;
			}
			if (streamingInProgress) {
				toast.error('Please wait for the current response to finish.');
				return;
			}
			onSubmit(prompt);
			textAreaRef.current.value = '';
		}
	};
	return (
		<textarea
			ref={textAreaRef}
			placeholder={placeholder}
			onKeyPress={handlePromptKeyPress}
			className={css(s.promptInput)}
		/>
	);
}

export function removeErrorMessageFromConversationCache(
	conversationId: string,
	impersonationContext?: Api.IImpersonationContext
) {
	const conversationQueryKey = getConversationChatMessageQueryKey({
		id: conversationId,
		impersonationContext,
	});
	const messages: Api.AIChatMessage[] = reactQueryClient.getQueryData(conversationQueryKey) ?? [];
	if ((messages.at(-1) as AIChatMessageError)?.error) {
		reactQueryClient.setQueryData(conversationQueryKey, messages.slice(0, -1));
	}
}

export function AIChatAssistantMessageError({ onTryAgain }: { onTryAgain: () => void }) {
	return (
		<AIChatAssistantMessage>
			<div className={css(s.errorMessageContainer)}>
				<div>Error</div>
				<p className={css(s.errorMessageDetails)}>Something went wrong with the assistant. Please try again.</p>
				<Button variant='destructive-secondary' size='sm' onClick={onTryAgain} styles={[s.errorMessageButton]}>
					Try Again
				</Button>
			</div>
		</AIChatAssistantMessage>
	);
}

export function GoogleReviewReplyAssistantChat({
	isOpen,
	onUseGoogleReviewReply,
	context,
	impersonationContext,
	onClose,
}: {
	isOpen: boolean;
	onUseGoogleReviewReply: (reply: string, sessionId?: string) => void;
	context?: {
		review: Api.IGoogleBusinessReview;
		companyName: string;
		reply: string;
	};
	impersonationContext?: Api.IImpersonationContext;
	onClose?: () => void;
}) {
	const id = useId();
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const { conversationId, aiConversationChatMessageQuery } = useAssistantConversation(
		GOOGLE_BUSINESS_REVIEW_REPLY_ASSISTANT,
		isOpen,
		impersonationContext
	);
	const aiAssistantStreamingMutation = useAIAssistantStreamingMutation({
		assistantName: GOOGLE_BUSINESS_REVIEW_REPLY_ASSISTANT,
	});
	const handleTryAgain = (chatRequest: Api.AIChatRequest) => {
		removeErrorMessageFromConversationCache(conversationId, impersonationContext);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest,
			impersonationContext,
		});
	};
	const handlePromptSubmitClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			ev.stopPropagation();
			ev.preventDefault();
			aiAssistantStreamingMutation.reset();
		}
	};
	const handleTranscriptionChange = () => {
		const form = textareaRef.current?.form;
		if (form) {
			form.requestSubmit();
		}
	};
	const handlePromptSubmit = ({ text }: PromptInputMessage) => {
		if (!text) {
			return;
		}
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			toast.error('Please wait for the current response to finish.');
			return;
		}
		removeErrorMessageFromConversationCache(conversationId, impersonationContext);
		queryClient.setQueryData(
			getConversationChatMessageQueryKey({
				id: conversationId,
				impersonationContext,
			}),
			(prevMessages: Api.AIChatMessage[]) => [...(prevMessages ?? []), { role: Api.AIChatRole.User, text, id: null }]
		);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest: {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						text,
						id: null,
					},
				],
				contexts: context
					? [
							{
								_type: 'GoogleReviewAIContext',
								reviewedCompanyName: context.companyName,
								review: context.review,
								reply: context.reply,
							} as Api.GoogleReviewAIContext,
						]
					: undefined,
			} as Api.AIMessageRequest,
			impersonationContext,
		});
	};
	const handleCraftReplyMessageClick = () => {
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest: {
				_type: 'AIActionRequest',
				action: {
					_type: 'GoogleReviewReplyAction',
					reviewId: context.review.id,
				} as Api.GoogleReviewReplyAction,
				contexts: [
					{
						_type: 'GoogleReviewAIContext',
						reviewedCompanyName: context.companyName,
						review: context.review,
						reply: context.reply,
					} as Api.GoogleReviewAIContext,
				],
			} as Api.AIActionRequest,
			impersonationContext,
		});
	};
	if (!isOpen) {
		return null;
	}
	if (!conversationId) {
		return (
			<CampaignComposerAssistantLayout onClose={onClose}>
				<LoadingSpinner />
			</CampaignComposerAssistantLayout>
		);
	}
	return (
		<CampaignComposerAssistantLayout onClose={onClose}>
			<ChatConversation>
				<ChatConversationContent>
					<AIChatAssistantMessage>
						Hey {userSession.user.firstName}, I can help you craft a response. Do you have any specific instructions for
						this message?
					</AIChatAssistantMessage>
					{context?.review &&
					!aiConversationChatMessageQuery.data?.length &&
					!aiAssistantStreamingMutation.isLoading &&
					!aiAssistantStreamingMutation.isStreaming ? (
						<button className={css(bs.ctaButtonReverse, s.aiKitCta)} onClick={handleCraftReplyMessageClick}>
							<span>Craft Reply Message</span> <ChevronRight />
						</button>
					) : null}
					{aiConversationChatMessageQuery.data?.map((message, i) => {
						return (
							<AIConversationMessage
								key={`${id}-message-${message.id ?? i}`}
								message={message}
								onUseGoogleReviewReply={onUseGoogleReviewReply}
								onErrorTryAgain={handleTryAgain}
								conversationId={conversationId}
								impersonationContext={impersonationContext}
								showMessageToolbar={false}
							/>
						);
					})}
					{aiAssistantStreamingMutation.isLoading ? (
						<AIChatAssistantMessage>
							<ThinkingIndicator />
						</AIChatAssistantMessage>
					) : null}
				</ChatConversationContent>
				<ChatConversationScrollButton />
			</ChatConversation>
			<div className={css(s.inputContainer)}>
				<PromptInput onSubmit={handlePromptSubmit}>
					<PromptInputBody>
						<PromptInputTextarea ref={textareaRef} placeholder='Add Google Review Reply Prompt' />
					</PromptInputBody>
					<PromptInputFooter>
						<PromptInputTools>
							<PromptInputSpeechButton textareaRef={textareaRef} onTranscriptionChange={handleTranscriptionChange} />
						</PromptInputTools>
						<PromptInputSubmit status={aiAssistantStreamingMutation.status} onClick={handlePromptSubmitClick} />
					</PromptInputFooter>
				</PromptInput>
			</div>
		</CampaignComposerAssistantLayout>
	);
}

export function UniversalAssistantLayout({
	onClose,
	onNewChat,
	children,
}: {
	onClose?: () => void;
	onNewChat?: () => void;
	children: ReactNode;
}) {
	return (
		<div className={css(s.universalChatWindow)}>
			{onClose ? <CloseButton styles={[s.closeBtn]} onClick={onClose} /> : null}
			<div className={css(s.chatWindowHeader)}>
				<div className={css(s.chatWindowHeaderStart)}>
					<Avatar2 imgSrc={WorkingLionImageUrl} fallbackText='Leo Avatar' styleDeclaration={s.leoAvatar} />
					<SparklesIcon />
					<span className={css(s.chatWindowHeaderTitle)}>Ask Lev</span>
				</div>
				{onNewChat ? (
					<Tooltip>
						<TooltipTrigger asChild>
							<button className={css(s.plusIconButton)} onClick={onNewChat}>
								<PlusIcon fillColor={brandPrimary} />
							</button>
						</TooltipTrigger>
						<TooltipContent>New Chat</TooltipContent>
					</Tooltip>
				) : null}
			</div>
			<div className={css(s.chatContent)}>{children}</div>
		</div>
	);
}

interface UniversalAssistantChatProps {
	onUseAIEmail: (
		subject: string,
		content: Api.IRawRichTextContentState,
		audienceInfo?: Api.ContactAudienceInfo,
		aiReference?: Api.IAIReference
	) => void;
	onUseSocialContent: (content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => void;
	onUseGoogleReviewReply?: (reply: string, sessionId?: string) => void;
	onUseBlogContent: (subject: string, content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => void;
	onUseContact: (value: Api.AIContactReference) => void;
	onUseContactList: (contactList: Api.ContactListToolCall) => void;
	onNavigate?: (entityType: string, entityId: string, parentEntityType?: string, parentEntityId?: string) => void;
	onClose?: () => void;
	renderConversationStarters?: (
		conversationId: string,
		streamingMutation: ReturnType<typeof useAIAssistantStreamingMutation>
	) => ReactNode;
	impersonationContext?: Api.IImpersonationContext;
}

export const AIAssistantStreamingMutationContext = createContext<ReturnType<
	typeof useAIAssistantStreamingMutation
> | null>(null);

export function AIAssistantStreamingMutationProvider({ children }: { children: ReactNode }) {
	const aiAssistantStreamingMutation = useAIAssistantStreamingMutation({
		assistantName: UNIVERSAL_ASSISTANT_NAME,
	});
	return (
		<AIAssistantStreamingMutationContext.Provider value={aiAssistantStreamingMutation}>
			{children}
		</AIAssistantStreamingMutationContext.Provider>
	);
}

export function AdminAgentStreamingMutationProvider({ children }: { children: ReactNode }) {
	const aiAssistantStreamingMutation = useAIAssistantStreamingMutation({
		assistantName: ADMIN_AGENT_NAME,
	});
	return (
		<AIAssistantStreamingMutationContext.Provider value={aiAssistantStreamingMutation}>
			{children}
		</AIAssistantStreamingMutationContext.Provider>
	);
}

export function SuccessMeetingPrepStreamingMutationProvider({ children }: { children: ReactNode }) {
	const aiAssistantStreamingMutation = useAIAssistantStreamingMutation({
		assistantName: SUCCESS_MEETING_PREP_AGENT_NAME,
	});
	return (
		<AIAssistantStreamingMutationContext.Provider value={aiAssistantStreamingMutation}>
			{children}
		</AIAssistantStreamingMutationContext.Provider>
	);
}

interface UniversalAssistantChatProps {
	onUseAIEmail: (
		subject: string,
		content: Api.IRawRichTextContentState,
		audienceInfo?: Api.ContactAudienceInfo,
		aiReference?: Api.IAIReference
	) => void;
	onUseSocialContent: (content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => void;
	onUseGoogleReviewReply?: (reply: string, sessionId?: string) => void;
	onUseBlogContent: (subject: string, content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => void;
	onUseContact: (value: Api.AIContactReference) => void;
	onUseContactList: (contactList: Api.ContactListToolCall) => void;
	onNavigate?: (entityType: string, entityId: string, parentEntityType?: string, parentEntityId?: string) => void;
	onClose?: () => void;
	renderConversationStarters?: (
		conversationId: string,
		streamingMutation: ReturnType<typeof useAIAssistantStreamingMutation>
	) => ReactNode;
}

export function UniversalAssistantChat({
	onUseAIEmail,
	onUseSocialContent,
	onUseGoogleReviewReply,
	onUseBlogContent,
	onUseContact,
	onUseContactList,
	onNavigate,
	onClose,
	renderConversationStarters,
	impersonationContext,
}: UniversalAssistantChatProps) {
	const id = useId();
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('UniversalAssistantChat');
	const fullscreenModal = useFullscreenModal();
	const brokerDealer = userSession.account?.additionalInfo?.brokerDealer;
	const defaultEmailHandler = defaultOnUseAIEmail(fullscreenModal);
	const onUseAIEmailHandler = (
		subject: string,
		content: Api.IRawRichTextContentState,
		audienceInfo?: Api.ContactAudienceInfo,
		aiReference?: Api.IAIReference
	) => {
		// Call the callback first, then show the toast to avoid interfering with modal state
		(onUseAIEmail ?? defaultEmailHandler)(subject, content, audienceInfo, aiReference);
		if (brokerDealer === Api.FinancialBrokerDealer.Cetera) {
			showCeteraAIDisclaimer();
		}
	};
	const onUseBlogContentHandler = (title: string, content: Api.IRawRichTextContentState) => {
		onUseBlogContent(title, content);
		if (brokerDealer === Api.FinancialBrokerDealer.Cetera) {
			showCeteraAIDisclaimer();
		}
	};
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	const aiConversationFilterQuery = useAIConversationFilterQuery({
		filter: {
			criteria: [
				{
					property: Api.AIConversationFilterProperty.ConversationType,
					value: Api.AIConversationType.SiteWide,
				},
			],
		},
	});
	const aiConversationChatMessageQuery = useAIConversationChatMessageQuery({
		enabled: Boolean(aiConversationFilterQuery.data?.length),
		id: aiConversationFilterQuery.data?.[0]?.id,
		staleTime: Infinity,
	});
	const { mutate: createUniversalAssistantConversation } = useAIAssistantCreateConversationMutation({
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-SiteWide-Assistant-Error`, error);
		},
	});

	const handleNewChatClick = () => {
		createUniversalAssistantConversation({
			assistantName: UNIVERSAL_ASSISTANT_NAME,
			impersonationContext,
		});
	};

	useEffect(() => {
		if (aiConversationFilterQuery.isSuccess && !aiConversationFilterQuery.data?.length) {
			createUniversalAssistantConversation({
				assistantName: UNIVERSAL_ASSISTANT_NAME,
				impersonationContext,
			});
		}
	}, [
		aiConversationFilterQuery.isSuccess,
		aiConversationFilterQuery.data,
		createUniversalAssistantConversation,
		impersonationContext,
	]);

	const conversationId = aiConversationFilterQuery.data?.[0]?.id;

	const aiAssistantStreamingMutation = useContext(AIAssistantStreamingMutationContext);

	const handleTryAgain = (chatRequest: Api.AIChatRequest) => {
		removeErrorMessageFromConversationCache(conversationId, impersonationContext);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest,
			impersonationContext,
		});
	};
	const handlePromptSubmitClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			ev.stopPropagation();
			ev.preventDefault();
			aiAssistantStreamingMutation.reset();
		}
	};
	const handleTranscriptionChange = () => {
		const form = textareaRef.current?.form;
		if (form) {
			form.requestSubmit();
		}
	};
	const handlePromptSubmit = ({ text }: PromptInputMessage) => {
		if (!text) {
			return;
		}
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			toast.error('Please wait for the current response to finish.');
			return;
		}
		removeErrorMessageFromConversationCache(conversationId, impersonationContext);
		queryClient.setQueryData(
			getConversationChatMessageQueryKey({
				id: conversationId,
				impersonationContext,
			}),
			(prevMessages: Api.AIChatMessage[]) => [...(prevMessages ?? []), { role: Api.AIChatRole.User, text, id: null }]
		);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest: {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						text,
						id: null,
					},
				],
				impersonationContext,
			} as Api.AIMessageRequest,
		});
	};

	if (!aiConversationFilterQuery?.data?.length) {
		return (
			<UniversalAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
				<LoadingSpinner />
			</UniversalAssistantLayout>
		);
	}
	return (
		<UniversalAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
			<ChatConversation>
				<ChatConversationContent>
					<AIChatAssistantMessage>Hey {userSession.user.firstName}, how can I help you today?</AIChatAssistantMessage>
					{renderConversationStarters &&
					!aiConversationChatMessageQuery.data?.length &&
					!aiAssistantStreamingMutation.isLoading &&
					!aiAssistantStreamingMutation.isStreaming
						? renderConversationStarters(conversationId, aiAssistantStreamingMutation)
						: null}
					{aiConversationChatMessageQuery.data?.map((message, i) => {
						return (
							<AIConversationMessage
								key={`${id}-message-${message.id ?? i}`}
								message={message}
								onUseAIEmail={onUseAIEmailHandler}
								onUseContact={onUseContact}
								onUseContactList={onUseContactList}
								onUseSocialContent={onUseSocialContent}
								onUseGoogleReviewReply={onUseGoogleReviewReply}
								onUseBlogContent={onUseBlogContentHandler}
								onNavigate={onNavigate}
								onErrorTryAgain={handleTryAgain}
								conversationId={conversationId}
								impersonationContext={impersonationContext}
								showMessageToolbar={false}
							/>
						);
					})}
					{aiAssistantStreamingMutation.isLoading ? (
						<AIChatAssistantMessage>
							<ThinkingIndicator />
						</AIChatAssistantMessage>
					) : null}
				</ChatConversationContent>
				<ChatConversationScrollButton />
			</ChatConversation>
			<div className={css(s.inputContainer)}>
				<PromptInput onSubmit={handlePromptSubmit}>
					<PromptInputBody>
						<PromptInputTextarea ref={textareaRef} />
					</PromptInputBody>
					<PromptInputFooter>
						<PromptInputTools>
							<PromptInputSpeechButton textareaRef={textareaRef} onTranscriptionChange={handleTranscriptionChange} />
						</PromptInputTools>
						<PromptInputSubmit status={aiAssistantStreamingMutation.status} onClick={handlePromptSubmitClick} />
					</PromptInputFooter>
				</PromptInput>
			</div>
		</UniversalAssistantLayout>
	);
}

export function UniversalAssistantChatWithinComposer({
	onUseAIEmail,
	onUseSocialContent,
	onUseBlogContent,
	onClose,
	renderConversationStarters,
	emailContext,
	socialContext,
	blogContext,
	isOpen,
	impersonationContext,
}: {
	onUseAIEmail?: (
		subject: string,
		content: Api.IRawRichTextContentState,
		audienceInfo?: Api.ContactAudienceInfo,
		aiReference?: Api.IAIReference
	) => void;
	onUseSocialContent?: (content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => void;
	onUseBlogContent?: (title: string, content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => void;
	onClose: () => void;
	emailContext?: { subject: string; body: Api.IRawRichTextContentState };
	socialContext?: { content: Api.IRawRichTextContentState };
	blogContext?: { title: string; content: Api.IRawRichTextContentState };
	renderConversationStarters?: (
		conversationId: string,
		streamingMutation: ReturnType<typeof useAIAssistantStreamingMutation>
	) => ReactNode;
	isOpen: boolean;
	impersonationContext?: Api.IImpersonationContext;
}) {
	const id = useId();
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const errorMessages = useErrorMessages();
	const fullscreenModal = useFullscreenModal();
	const history = useHistory();
	const logger = useEventLogging('UniversalAssistantChat');
	const brokerDealer = userSession.account?.additionalInfo?.brokerDealer;
	const defaultEmailHandler = defaultOnUseAIEmail(fullscreenModal);
	const onUseAIEmailHandler = (
		subject: string,
		content: Api.IRawRichTextContentState,
		audienceInfo?: Api.ContactAudienceInfo,
		aiReference?: Api.IAIReference
	) => {
		// Call the callback first, then show the toast to avoid interfering with modal state
		(onUseAIEmail ?? defaultEmailHandler)(subject, content, audienceInfo, aiReference);
		if (brokerDealer === Api.FinancialBrokerDealer.Cetera) {
			showCeteraAIDisclaimer();
		}
	};
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const onUseSocialContentHandler = onUseSocialContent ?? defaultOnUseSocialContent(fullscreenModal);
	// Google revies are diabled because we don't have a path for it yet
	const onUseGoogleReviewReply: (reply: string) => void = undefined;
	const defaultBlogHandler = onUseBlogContent ?? defaultOnUseBlogContent(fullscreenModal);
	const onUseBlogContentHandler = (title: string, content: Api.IRawRichTextContentState) => {
		defaultBlogHandler(title, content);
		if (brokerDealer === Api.FinancialBrokerDealer.Cetera) {
			showCeteraAIDisclaimer();
		}
	};
	const onUseContact = (contactInfo: Api.AIContactReference) => {
		history.push(`${ROUTES.contacts.people}/${contactInfo.id}`);
	};
	const onUseContactList = (contactList: Api.ContactListToolCall) => {
		const searchParams = searchRequestToQueryStringParams(
			{
				filter: { operator: 0, criteria: contactList.filters },
			},
			{ sort: 'asc', sortBy: 'handle' }
		);
		history.push(`${ROUTES.contacts.people}/?search=${searchParams.search}&sort=${searchParams.sort}`);
	};

	const aiConversationFilterQuery = useAIConversationFilterQuery({
		impersonationContext,
		filter: {
			criteria: [
				{
					property: Api.AIConversationFilterProperty.ConversationType,
					value: Api.AIConversationType.SiteWide,
				},
			],
		},
	});
	const aiConversationChatMessageQuery = useAIConversationChatMessageQuery({
		impersonationContext,
		enabled: Boolean(aiConversationFilterQuery.data?.length),
		id: aiConversationFilterQuery.data?.[0]?.id,
		staleTime: Infinity,
	});
	const createUniversalAssistantConversationMutation = useAIAssistantCreateConversationMutation({
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-SiteWide-Assistant-Error`, error);
		},
	});

	const handleNewChatClick = () => {
		createUniversalAssistantConversationMutation.mutate({
			assistantName: UNIVERSAL_ASSISTANT_NAME,
			impersonationContext,
		});
	};

	const aiAssistantStreamingMutation = useContext(AIAssistantStreamingMutationContext);

	if (!aiAssistantStreamingMutation) {
		throw new Error('UniversalAssistantChatWithinComposer must be wrapped in AIAssistantStreamingMutationProvider');
	}

	const handleTryAgain = (chatRequest: Api.AIChatRequest) => {
		removeErrorMessageFromConversationCache(conversationId);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest,
			impersonationContext,
		});
	};
	const handlePromptSubmitClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			ev.stopPropagation();
			ev.preventDefault();
			aiAssistantStreamingMutation.reset();
		}
	};
	const handleTranscriptionChange = () => {
		const form = textareaRef.current?.form;
		if (form) {
			form.requestSubmit();
		}
	};
	const handlePromptSubmit = ({ text }: PromptInputMessage) => {
		if (!text) {
			return;
		}
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			toast.error('Please wait for the current response to finish.');
			return;
		}
		removeErrorMessageFromConversationCache(conversationId);
		queryClient.setQueryData(
			getConversationChatMessageQueryKey({
				id: conversationId,
				impersonationContext,
			}),
			(prevMessages: Api.AIChatMessage[]) => [...(prevMessages ?? []), { role: Api.AIChatRole.User, text, id: null }]
		);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			impersonationContext,
			chatRequest: {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						text,
						id: null,
					},
				],
				contexts: (() => {
					const contexts = [
						...(emailContext
							? [
									{
										_type: 'EmailAIContext',
										subject: emailContext.subject,
										body: emailContext.body,
									} as Api.EmailAIContext,
								]
							: []),
						...(socialContext
							? [
									{
										_type: 'SocialMediaAIContext',
										content: socialContext.content,
									} as Api.SocialMediaAIContext,
								]
							: []),
						...(blogContext
							? [
									{
										_type: 'BlogAIContext',
										title: blogContext.title,
										content: blogContext.content,
									} as Api.BlogAIContext,
								]
							: []),
					];
					return contexts.length > 0 ? contexts : undefined;
				})(),
			} as Api.AIMessageRequest,
		});
	};
	if (!isOpen) {
		return null;
	}
	if (!aiConversationFilterQuery?.data?.length) {
		return (
			<CampaignComposerAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
				<LoadingSpinner />
			</CampaignComposerAssistantLayout>
		);
	}
	const conversationId = aiConversationFilterQuery.data[0].id;
	return (
		<CampaignComposerAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
			<ChatConversation>
				<ChatConversationContent>
					<AIChatAssistantMessage>Hey {userSession.user.firstName}, how can I help you today?</AIChatAssistantMessage>
					{renderConversationStarters &&
					!aiConversationChatMessageQuery.data?.length &&
					!aiAssistantStreamingMutation.isLoading &&
					!aiAssistantStreamingMutation.isStreaming
						? renderConversationStarters(conversationId, aiAssistantStreamingMutation)
						: null}
					{aiConversationChatMessageQuery.data?.map((message, i) => {
						return (
							<AIConversationMessage
								key={`${id}-message-${message.id ?? i}`}
								message={message}
								onUseAIEmail={onUseAIEmailHandler}
								onUseContact={onUseContact}
								onUseContactList={onUseContactList}
								onUseSocialContent={onUseSocialContentHandler}
								onUseGoogleReviewReply={onUseGoogleReviewReply}
								onUseBlogContent={onUseBlogContentHandler}
								onErrorTryAgain={handleTryAgain}
								conversationId={conversationId}
								impersonationContext={impersonationContext}
								showMessageToolbar={false}
							/>
						);
					})}
					{aiAssistantStreamingMutation.isLoading ? (
						<AIChatAssistantMessage>
							<ThinkingIndicator />
						</AIChatAssistantMessage>
					) : null}
				</ChatConversationContent>
				<ChatConversationScrollButton />
			</ChatConversation>
			<div className={css(s.inputContainer)}>
				<PromptInput onSubmit={handlePromptSubmit}>
					<PromptInputBody>
						<PromptInputTextarea ref={textareaRef} placeholder='Send a message...' />
					</PromptInputBody>
					<PromptInputFooter>
						<PromptInputTools>
							<PromptInputSpeechButton textareaRef={textareaRef} onTranscriptionChange={handleTranscriptionChange} />
						</PromptInputTools>
						<PromptInputSubmit status={aiAssistantStreamingMutation.status} onClick={handlePromptSubmitClick} />
					</PromptInputFooter>
				</PromptInput>
			</div>
		</CampaignComposerAssistantLayout>
	);
}

/**
 * Maps string property names from AI SimpleFilter to ContactFilterCriteriaProperty enum values.
 * This is needed because the AI returns property names as strings, but the API expects enum values.
 */
const SIMPLE_FILTER_PROPERTY_MAP: Record<string, Api.ContactFilterCriteriaProperty> = {
	Tag: Api.ContactFilterCriteriaProperty.Tag,
	City: Api.ContactFilterCriteriaProperty.City,
	StateProvince: Api.ContactFilterCriteriaProperty.StateProvince,
	PostalCode: Api.ContactFilterCriteriaProperty.PostalCode,
	CompanyTag: Api.ContactFilterCriteriaProperty.CompanyTag,
};

/**
 * Converts a SimpleFilterCriteria to IContactFilterCriteria if the property is supported.
 * Returns undefined if the property is not recognized.
 */
function convertSimpleFilterCriteria(criteria: {
	property: string;
	value: string;
}): Api.IContactFilterCriteria | undefined {
	const enumProperty = SIMPLE_FILTER_PROPERTY_MAP[criteria.property];
	if (enumProperty === undefined) {
		return undefined;
	}
	return {
		property: enumProperty,
		value: criteria.value,
	};
}

/**
 * Converts ContactAudienceInfo from the AI to contact selection data for campaign creation.
 * Supports both tagFilters (for dynamic audience) and contactIds (for explicit contacts).
 * Handles Tag, City, StateProvince, PostalCode, and CompanyTag filter properties.
 *
 * CRITICAL: The array of tagFilters is OR'd together (UNION), not AND'd (INTERSECTION).
 * - Multiple filters in array → OR logic (e.g., "VC" OR "VC 2024" OR "VC: Stage 2")
 * - Multiple criteria in single filter → AND logic (e.g., "VIP" AND "Q4")
 */
function convertAudienceInfoToContactSelection(audienceInfo: Api.ContactAudienceInfo): {
	bulkFilterRequest?: Api.IEmailMessageContactsFilterRequest;
	contactIds?: string[];
} | null {
	// Handle tagFilters - for dynamic audience selection (also includes location filters)
	if (audienceInfo.tagFilters?.length) {
		// SCENARIO 1: Multiple filters → OR logic (UNION)
		// e.g., [{Tag: "VC"}, {Tag: "VC 2024"}, {Tag: "VC: Stage 2"}]
		// Result: Contacts with "VC" OR "VC 2024" OR "VC: Stage 2"
		if (audienceInfo.tagFilters.length > 1) {
			// Each filter becomes a separate criteria item, wrapped in OR operator
			const orCriteria: Api.IContactFilterCriteria[] = audienceInfo.tagFilters
				.map(filter => {
					// Convert each criterion using the property map
					const filterCriteria = filter.criteria
						.map(convertSimpleFilterCriteria)
						.filter((c): c is Api.IContactFilterCriteria => c !== undefined);

					if (filterCriteria.length === 0) {
						return undefined;
					}

					// If the filter has multiple criteria, AND them together
					if (filterCriteria.length > 1) {
						return {
							op: Api.CONTACT_FILTER_OPERATOR.And,
							criteria: filterCriteria,
						};
					}
					// Single criterion - return it directly
					return filterCriteria[0];
				})
				.filter((c): c is Api.IContactFilterCriteria => c !== undefined);

			if (orCriteria.length === 0) {
				return null;
			}

			// If only one filter after processing, don't wrap in OR
			if (orCriteria.length === 1) {
				return {
					bulkFilterRequest: {
						contactFilterRequest: {
							criteria: [orCriteria[0]],
						},
					},
				};
			}

			// Wrap all filters in an OR operation
			return {
				bulkFilterRequest: {
					contactFilterRequest: {
						op: Api.CONTACT_FILTER_OPERATOR.Or,
						criteria: orCriteria,
					},
				},
			};
		}

		// SCENARIO 2: Single filter with multiple criteria → AND logic (INTERSECTION)
		// e.g., [{criteria: [{Tag: "VIP"}, {Tag: "Q4"}]}]
		// or [{criteria: [{City: "Boston"}, {StateProvince: "MA"}]}]
		// Result: Contacts with "VIP" AND "Q4", or in Boston AND MA
		const singleFilter = audienceInfo.tagFilters[0];
		const criteria = singleFilter.criteria
			.map(convertSimpleFilterCriteria)
			.filter((c): c is Api.IContactFilterCriteria => c !== undefined);

		if (criteria.length === 0) {
			return null;
		}

		if (criteria.length > 1) {
			// Multiple criteria in single filter → AND them together
			return {
				bulkFilterRequest: {
					contactFilterRequest: {
						op: Api.CONTACT_FILTER_OPERATOR.And,
						criteria,
					},
				},
			};
		}

		// SCENARIO 3: Single filter with single criterion → just use it
		return {
			bulkFilterRequest: {
				contactFilterRequest: {
					criteria: [criteria[0]],
				},
			},
		};
	}

	// Handle contactIds - for explicit contact selection
	if (audienceInfo.contactIds?.length) {
		return {
			contactIds: audienceInfo.contactIds,
		};
	}

	return null;
}

const defaultOnUseAIEmail =
	(fullscreenModal: FullScreenModalViewModel) =>
	(
		subject: string,
		content: Api.IRawRichTextContentState,
		audienceInfo?: Api.ContactAudienceInfo,
		aiReference?: Api.IAIReference
	) => {
		const contactSelection = audienceInfo ? convertAudienceInfoToContactSelection(audienceInfo) : null;

		fullscreenModal.history.push({
			pathname: '/email/campaigns/create/from-scratch',
			state: {
				model: {
					context: {
						allowSenderSelection: true,
						...(contactSelection || {}),
						subject,
						body: content,
						aiReference,
					},
				},
			},
		});
	};
const defaultOnUseSocialContent =
	(fullscreenModal: FullScreenModalViewModel) =>
	(content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => {
		fullscreenModal.history.push({
			pathname: `/social-media/post/create`,
			state: {
				initialContent: content,
				aiReference,
			},
		});
	};
const defaultOnUseBlogContent =
	(fullscreenModal: FullScreenModalViewModel) =>
	(subject: string, content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => {
		fullscreenModal.history.push({
			pathname: `/blog/post/create`,
			state: {
				initialContent: content,
				initialSubject: subject,
				aiReference,
			},
		});
	};

const UniversalAssistantChatErrorBoundary = ({ children }: { children: ReactNode }) => {
	const logger = useEventLogging('AssistantChatErrorBoundary');
	const errorMessages = useErrorMessages();
	const { mutate: createUniversalAssistantConversation } = useAIAssistantCreateConversationMutation({
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-SiteWide-Assistant-Error`, error);
		},
	});
	const handleNewChatClick = () => {
		createUniversalAssistantConversation({
			assistantName: UNIVERSAL_ASSISTANT_NAME,
		});
	};
	return (
		<ErrorBoundary
			fallback={
				<UniversalAssistantLayout onNewChat={handleNewChatClick}>
					<div className={css(bs.flex, bs.flexCol, bs.itemsCenter, bs.justifyCenter, bs.p2, bs.hFull)}>
						<WarningIcon />
						<div className={css(bs.fontBold, bs.textBrandPrimary, bs.mt4)}>Error occurred loading your chat</div>
						<Button variant='secondary' styles={[bs.mt4]} onClick={handleNewChatClick}>
							Start new chat
						</Button>
					</div>
				</UniversalAssistantLayout>
			}
			onError={(error: Error) => {
				logger.logApiError('AssistantChatErrorBoundary-Error', error as Api.IOperationResultNoValue);
			}}
		>
			{children}
		</ErrorBoundary>
	);
};

export function DefaultUniversalAssistantChat(props: Partial<UniversalAssistantChatProps>) {
	const fullscreenModal = useFullscreenModal();
	const history = useHistory();
	return (
		<UniversalAssistantChatErrorBoundary>
			<UniversalAssistantChat
				onUseAIEmail={defaultOnUseAIEmail(fullscreenModal)}
				onUseSocialContent={defaultOnUseSocialContent(fullscreenModal)}
				/* onUseGoogleReviewReply={_reply => {
			}} */
				onUseBlogContent={defaultOnUseBlogContent(fullscreenModal)}
				onUseContact={contactInfo => {
					history.push(`${ROUTES.contacts.people}/${contactInfo.id}`);
				}}
				onUseContactList={contactList => {
					const searchParams = searchRequestToQueryStringParams(
						{
							filter: { operator: 0, criteria: contactList.filters },
						},
						{ sort: 'asc', sortBy: 'handle' }
					);
					history.push(`${ROUTES.contacts.people}/?search=${searchParams.search}&sort=${searchParams.sort}`);
				}}
				onNavigate={(entityType, entityId, parentEntityType, parentEntityId) => {
					const path = getNavigationPath(
						entityType as NavigationEntityType,
						entityId,
						parentEntityType as NavigationEntityType,
						parentEntityId
					);
					history.push(path);
				}}
				{...props}
			/>
		</UniversalAssistantChatErrorBoundary>
	);
}

interface AdminAppAssistantChatProps {
	impersonationContext: Api.IImpersonationContext;
	onUseAIEmail?: (
		subject: string,
		content: Api.IRawRichTextContentState,
		audienceInfo?: Api.ContactAudienceInfo,
		aiReference?: Api.IAIReference
	) => void;
	onUseSocialContent?: (content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => void;
	onUseGoogleReviewReply?: (reply: string, sessionId?: string) => void;
	onUseBlogContent?: (subject: string, content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => void;
	onUseContact?: (value: {
		firstName?: string;
		lastName?: string;
		email?: string;
		phoneNumber?: string;
		company?: string;
	}) => void;
	onClose?: () => void;
	renderConversationStarters?: (
		conversationId: string,
		streamingMutation: ReturnType<typeof useAIAssistantStreamingMutation>
	) => ReactNode;
}

const AdminAssistantChatErrorBoundary = ({
	children,
	impersonationContext,
}: {
	children: ReactNode;
	impersonationContext: Api.IImpersonationContext;
}) => {
	const logger = useEventLogging('AssistantChatErrorBoundary');
	const errorMessages = useErrorMessages();
	const { mutate: createUniversalAssistantConversation } = useAIAssistantCreateConversationMutation({
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-AdminAgent-Assistant-Error`, error);
		},
	});
	const handleNewChatClick = () => {
		createUniversalAssistantConversation({
			assistantName: ADMIN_AGENT_NAME,
			impersonationContext,
		});
	};
	return (
		<ErrorBoundary
			fallback={
				<UniversalAssistantLayout onNewChat={handleNewChatClick}>
					<div className={css(bs.flex, bs.flexCol, bs.itemsCenter, bs.justifyCenter, bs.p2, bs.hFull)}>
						<WarningIcon />
						<div className={css(bs.fontBold, bs.textBrandPrimary, bs.mt4)}>Error occurred loading your chat</div>
						<Button variant='secondary' styles={[bs.mt4]} onClick={handleNewChatClick}>
							Start new chat
						</Button>
					</div>
				</UniversalAssistantLayout>
			}
			onError={(error: Error) => {
				logger.logApiError('AssistantChatErrorBoundary-Error', error as Api.IOperationResultNoValue);
			}}
		>
			{children}
		</ErrorBoundary>
	);
};

export function AdminAppAssistantChat(props: AdminAppAssistantChatProps) {
	return (
		<AdminAssistantChatErrorBoundary impersonationContext={props.impersonationContext}>
			<AdminAssistantChat {...props} />
		</AdminAssistantChatErrorBoundary>
	);
}

export function AdminAssistantChat({
	onUseAIEmail: onUseAIEmailProp,
	onUseSocialContent: onUseSocialContentProp,
	onUseGoogleReviewReply,
	onUseBlogContent: onUseBlogContentProp,
	onClose,
	renderConversationStarters,
	impersonationContext,
}: AdminAppAssistantChatProps) {
	const id = useId();
	const fullscreenModal = useFullscreenModal();
	const onUseAIEmail = onUseAIEmailProp ?? defaultOnUseAIEmail(fullscreenModal);
	const onUseSocialContent = onUseSocialContentProp ?? defaultOnUseSocialContent(fullscreenModal);
	const onUseBlogContent = onUseBlogContentProp ?? defaultOnUseBlogContent(fullscreenModal);
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('AdminAppAssistantChat');
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const accountQuery = useAdminAccountQuery({
		accountId: impersonationContext.account.id,
	});
	const aiConversationFilterQuery = useAIConversationFilterQuery({
		filter: {
			criteria: [
				{
					property: Api.AIConversationFilterProperty.ConversationType,
					value: Api.AIConversationType.Admin,
				},
			],
		},
		impersonationContext,
	});
	const aiConversationChatMessageQuery = useAIConversationChatMessageQuery({
		enabled: Boolean(aiConversationFilterQuery.data?.length),
		id: aiConversationFilterQuery.data?.[0]?.id,
		staleTime: Infinity,
		impersonationContext,
	});
	const { mutate: createUniversalAssistantConversation } = useAIAssistantCreateConversationMutation({
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-AdminAgent-Assistant-Error`, error);
		},
	});
	const handleNewChatClick = () => {
		createUniversalAssistantConversation({
			assistantName: ADMIN_AGENT_NAME,
			impersonationContext,
		});
	};
	const impersonationContextRef = useRef(impersonationContext);
	impersonationContextRef.current = impersonationContext;
	useEffect(() => {
		if (aiConversationFilterQuery.isSuccess && !aiConversationFilterQuery.data?.length) {
			createUniversalAssistantConversation({
				assistantName: ADMIN_AGENT_NAME,
				impersonationContext: impersonationContextRef.current,
			});
		}
	}, [aiConversationFilterQuery.isSuccess, aiConversationFilterQuery.data, createUniversalAssistantConversation]);

	const aiAssistantStreamingMutation = useContext(AIAssistantStreamingMutationContext);

	const handleTryAgain = (chatRequest: Api.AIChatRequest) => {
		removeErrorMessageFromConversationCache(conversationId);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest,
			impersonationContext,
		});
	};
	const handlePromptSubmitClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			ev.stopPropagation();
			ev.preventDefault();
			aiAssistantStreamingMutation.reset();
		}
	};
	const handleTranscriptionChange = () => {
		const form = textareaRef.current?.form;
		if (form) {
			form.requestSubmit();
		}
	};
	const handlePromptSubmit = ({ text }: PromptInputMessage) => {
		if (!text) {
			return;
		}
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			toast.error('Please wait for the current response to finish.');
			return;
		}
		removeErrorMessageFromConversationCache(conversationId);
		queryClient.setQueryData(
			getConversationChatMessageQueryKey({
				id: conversationId,
				impersonationContext,
			}),
			(prevMessages: Api.AIChatMessage[]) => [...(prevMessages ?? []), { role: Api.AIChatRole.User, text, id: null }]
		);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest: {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						text,
						id: null,
					},
				],
			} as Api.AIMessageRequest,
			impersonationContext,
		});
	};

	if (!aiConversationFilterQuery?.data?.length) {
		return (
			<UniversalAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
				<LoadingSpinner />
			</UniversalAssistantLayout>
		);
	}

	const conversationId = aiConversationFilterQuery.data[0].id;
	return (
		<UniversalAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
			<ChatConversation>
				<ChatConversationContent>
					<AIChatAssistantMessage>
						Hey {userSession.user.firstName}, how can I help you with{' '}
						{`${accountQuery.data?.companyName ?? 'this account'}`}?
					</AIChatAssistantMessage>
					{renderConversationStarters &&
					!aiConversationChatMessageQuery.data?.length &&
					!aiAssistantStreamingMutation.isLoading &&
					!aiAssistantStreamingMutation.isStreaming
						? renderConversationStarters(conversationId, aiAssistantStreamingMutation)
						: null}
					{aiConversationChatMessageQuery.data?.map((message, i) => {
						return (
							<AIConversationMessage
								key={`${id}-message-${message.id ?? i}`}
								message={message}
								onUseAIEmail={onUseAIEmail}
								onUseSocialContent={onUseSocialContent}
								onUseGoogleReviewReply={onUseGoogleReviewReply}
								onUseBlogContent={onUseBlogContent}
								onErrorTryAgain={handleTryAgain}
								conversationId={conversationId}
								impersonationContext={impersonationContext}
							/>
						);
					})}
					{aiAssistantStreamingMutation.isLoading ? (
						<AIChatAssistantMessage>
							<ThinkingIndicator />
						</AIChatAssistantMessage>
					) : null}
				</ChatConversationContent>
				<ChatConversationScrollButton />
			</ChatConversation>
			<div className={css(s.inputContainer)}>
				<PromptInput onSubmit={handlePromptSubmit}>
					<PromptInputBody>
						<PromptInputTextarea ref={textareaRef} placeholder='Send a message...' />
					</PromptInputBody>
					<PromptInputFooter>
						<PromptInputTools>
							<PromptInputSpeechButton textareaRef={textareaRef} onTranscriptionChange={handleTranscriptionChange} />
						</PromptInputTools>
						<PromptInputSubmit status={aiAssistantStreamingMutation.status} onClick={handlePromptSubmitClick} />
					</PromptInputFooter>
				</PromptInput>
			</div>
		</UniversalAssistantLayout>
	);
}

const SuccessMeetingPrepAssistantChatErrorBoundary = ({
	children,
	impersonationContext,
}: {
	children: ReactNode;
	impersonationContext: Api.IImpersonationContext;
}) => {
	const logger = useEventLogging('SuccessMeetingPrepAssistantChatErrorBoundary');
	const errorMessages = useErrorMessages();
	const { mutate: createSuccessMeetingPrepConversation } = useAIAssistantCreateConversationMutation({
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-SuccessMeetingPrep-Assistant-Error`, error);
		},
	});
	const handleNewChatClick = () => {
		createSuccessMeetingPrepConversation({
			assistantName: SUCCESS_MEETING_PREP_AGENT_NAME,
			impersonationContext,
		});
	};
	return (
		<ErrorBoundary
			fallback={
				<UniversalAssistantLayout onNewChat={handleNewChatClick}>
					<div className={css(bs.flex, bs.flexCol, bs.itemsCenter, bs.justifyCenter, bs.p2, bs.hFull)}>
						<WarningIcon />
						<div className={css(bs.fontBold, bs.textBrandPrimary, bs.mt4)}>Error occurred loading your chat</div>
						<Button variant='secondary' styles={[bs.mt4]} onClick={handleNewChatClick}>
							Start new chat
						</Button>
					</div>
				</UniversalAssistantLayout>
			}
			onError={(error: Error) => {
				logger.logApiError('SuccessMeetingPrepAssistantChatErrorBoundary-Error', error as Api.IOperationResultNoValue);
			}}
		>
			{children}
		</ErrorBoundary>
	);
};

export function SuccessMeetingPrepAppAssistantChat(props: AdminAppAssistantChatProps) {
	return (
		<SuccessMeetingPrepStreamingMutationProvider>
			<SuccessMeetingPrepAssistantChatErrorBoundary impersonationContext={props.impersonationContext}>
				<SuccessMeetingPrepAssistantChat {...props} />
			</SuccessMeetingPrepAssistantChatErrorBoundary>
		</SuccessMeetingPrepStreamingMutationProvider>
	);
}

export function SuccessMeetingPrepAssistantChat({
	onUseAIEmail: onUseAIEmailProp,
	onUseSocialContent: onUseSocialContentProp,
	onUseGoogleReviewReply,
	onUseBlogContent: onUseBlogContentProp,
	onClose,
	renderConversationStarters,
	impersonationContext,
}: AdminAppAssistantChatProps) {
	const id = useId();
	const fullscreenModal = useFullscreenModal();
	const onUseAIEmail = onUseAIEmailProp ?? defaultOnUseAIEmail(fullscreenModal);
	const onUseSocialContent = onUseSocialContentProp ?? defaultOnUseSocialContent(fullscreenModal);
	const onUseBlogContent = onUseBlogContentProp ?? defaultOnUseBlogContent(fullscreenModal);
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('SuccessMeetingPrepAssistantChat');
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const accountQuery = useAdminAccountQuery({
		accountId: impersonationContext.account.id,
	});
	const aiConversationFilterQuery = useAIConversationFilterQuery({
		filter: {
			criteria: [
				{
					property: Api.AIConversationFilterProperty.ConversationType,
					value: Api.AIConversationType.SuccessMeetingPrep,
				},
			],
		},
		impersonationContext,
	});
	const aiConversationChatMessageQuery = useAIConversationChatMessageQuery({
		enabled: Boolean(aiConversationFilterQuery.data?.length),
		id: aiConversationFilterQuery.data?.[0]?.id,
		staleTime: Infinity,
		impersonationContext,
	});
	const { mutate: createSuccessMeetingPrepConversation } = useAIAssistantCreateConversationMutation({
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-SuccessMeetingPrep-Assistant-Error`, error);
		},
	});
	const handleNewChatClick = () => {
		createSuccessMeetingPrepConversation({
			assistantName: SUCCESS_MEETING_PREP_AGENT_NAME,
			impersonationContext,
		});
	};
	const impersonationContextRef = useRef(impersonationContext);
	impersonationContextRef.current = impersonationContext;
	useEffect(() => {
		if (aiConversationFilterQuery.isSuccess && !aiConversationFilterQuery.data?.length) {
			createSuccessMeetingPrepConversation({
				assistantName: SUCCESS_MEETING_PREP_AGENT_NAME,
				impersonationContext: impersonationContextRef.current,
			});
		}
	}, [aiConversationFilterQuery.isSuccess, aiConversationFilterQuery.data, createSuccessMeetingPrepConversation]);

	const aiAssistantStreamingMutation = useContext(AIAssistantStreamingMutationContext);
	const handleTryAgain = (chatRequest: Api.AIChatRequest) => {
		removeErrorMessageFromConversationCache(conversationId, impersonationContext);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest,
			impersonationContext,
		});
	};
	const handlePromptSubmitClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			ev.stopPropagation();
			ev.preventDefault();
			aiAssistantStreamingMutation.reset();
		}
	};
	const handleTranscriptionChange = () => {
		const form = textareaRef.current?.form;
		if (form) {
			form.requestSubmit();
		}
	};
	const handlePromptSubmit = ({ text }: PromptInputMessage) => {
		if (!text) {
			return;
		}
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			toast.error('Please wait for the current response to finish.');
			return;
		}
		removeErrorMessageFromConversationCache(conversationId, impersonationContext);
		queryClient.setQueryData(
			getConversationChatMessageQueryKey({
				id: conversationId,
				impersonationContext,
			}),
			(prevMessages: Api.AIChatMessage[]) => [...(prevMessages ?? []), { role: Api.AIChatRole.User, text, id: null }]
		);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest: {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						text,
						id: null,
					},
				],
			} as Api.AIMessageRequest,
			impersonationContext,
		});
	};
	if (!aiConversationFilterQuery?.data?.length) {
		return (
			<UniversalAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
				<LoadingSpinner />
			</UniversalAssistantLayout>
		);
	}
	const conversationId = aiConversationFilterQuery.data[0].id;
	return (
		<UniversalAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
			<ChatConversation>
				<ChatConversationContent>
					<AIChatAssistantMessage>
						Hey {userSession.user.firstName}, I&apos;ll help you prepare for your meeting with{' '}
						{`${accountQuery.data?.companyName ?? 'this account'}`}.
					</AIChatAssistantMessage>
					{renderConversationStarters &&
					!aiConversationChatMessageQuery.data?.length &&
					!aiAssistantStreamingMutation.isLoading &&
					!aiAssistantStreamingMutation.isStreaming
						? renderConversationStarters(conversationId, aiAssistantStreamingMutation)
						: null}
					{aiConversationChatMessageQuery.data?.map((message, i) => {
						return (
							<AIConversationMessage
								key={`${id}-message-${message.id ?? i}`}
								message={message}
								onUseAIEmail={onUseAIEmail}
								onUseSocialContent={onUseSocialContent}
								onUseGoogleReviewReply={onUseGoogleReviewReply}
								onUseBlogContent={onUseBlogContent}
								onErrorTryAgain={handleTryAgain}
								conversationId={conversationId}
								impersonationContext={impersonationContext}
							/>
						);
					})}
					{aiAssistantStreamingMutation.isLoading ? (
						<AIChatAssistantMessage>
							<ThinkingIndicator />
						</AIChatAssistantMessage>
					) : null}
				</ChatConversationContent>
				<ChatConversationScrollButton />
			</ChatConversation>
			<div className={css(s.inputContainer)}>
				<PromptInput onSubmit={handlePromptSubmit}>
					<PromptInputBody>
						<PromptInputTextarea ref={textareaRef} placeholder='Send a message...' />
					</PromptInputBody>
					<PromptInputFooter>
						<PromptInputTools>
							<PromptInputSpeechButton textareaRef={textareaRef} onTranscriptionChange={handleTranscriptionChange} />
						</PromptInputTools>
						<PromptInputSubmit status={aiAssistantStreamingMutation.status} onClick={handlePromptSubmitClick} />
					</PromptInputFooter>
				</PromptInput>
			</div>
		</UniversalAssistantLayout>
	);
}
